import { ThemeProvider } from '@mui/material/styles';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import type { RootState } from '@store/rootReducer';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createAppTheme } from '../theme';
import type { ThemeMode } from './palette';

/** What the user chose. `system` is a choice too, and it is the default. */
export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'cc-theme';

interface ThemeModeValue {
  /** What the user picked: light, dark, or "follow the OS". */
  preference: ThemePreference;
  /** What that resolves to right now. Never 'system'. */
  mode: ThemeMode;
  setPreference: (next: ThemePreference) => void;
}

const ThemeModeContext = createContext<ThemeModeValue | null>(null);

const isPreference = (v: unknown): v is ThemePreference =>
  v === 'light' || v === 'dark' || v === 'system';

/** Read the stored choice. Storage can be unavailable, and that is not an error. */
export function readStoredPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

function writeStoredPreference(next: ThemePreference) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    /* A full or blocked localStorage must never break the app; the choice just does not persist. */
  }
}

/*
 * The server stores the preference in upper case, as an enumeration; the client uses lower case
 * throughout because that is what the DOM attribute and the CSS selectors use. One place converts.
 */
const GET_UI_PREFS = '{ getAdminUiPreferences { themeMode } }';
const setUiPrefs = (mode: ThemePreference) =>
  `mutation { updateAdminUiPreferences(themeMode: "${mode.toUpperCase()}") { themeMode } }`;

const prefersLight = () => {
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches;
  } catch {
    return false;
  }
};

/**
 * The theme, and the one place that decides what it currently is.
 *
 * Context rather than redux, for two reasons. The value is needed before the store has hydrated —
 * the head script has already painted using it — and a redux slice would give the DOM attribute a
 * second writer, which is how an attribute and the state that is supposed to describe it drift
 * apart.
 *
 * The storage idiom is the one `useActivityRail` established: a lazy initialiser, try/catch on both
 * ends, and an explicit choice always beating the derived default.
 *
 * `system` is live, not read once. Someone who has their OS on a sunset schedule should not have to
 * reload the console at dusk, and a `change` listener is the whole cost of that.
 */
export function ThemeModeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [systemIsLight, setSystemIsLight] = useState<boolean>(prefersLight);

  useEffect(() => {
    let query: MediaQueryList;
    try {
      query = window.matchMedia('(prefers-color-scheme: light)');
    } catch {
      return undefined;
    }
    const onChange = (e: MediaQueryListEvent) => setSystemIsLight(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const mode: ThemeMode = preference === 'system' ? (systemIsLight ? 'light' : 'dark') : preference;

  /*
   * The attribute the stylesheets select on, and the color-scheme native controls read. The head
   * script sets both before first paint; this keeps them right for the rest of the session.
   */
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  /*
   * The server is the source of truth, and localStorage is how the NEXT first paint gets to be
   * right before the profile has loaded. So the value flows one way on arrival (server -> local)
   * and both ways on a change.
   */
  const profile = useSelector((state: RootState) => state.loginUserProfile.profile);
  const adopted = useRef(false);

  useEffect(() => {
    if (!profile || adopted.current) return;
    adopted.current = true;
    fetchGraphQLApiData(GET_UI_PREFS)
      .then((res) => {
        const stored = res?.data?.getAdminUiPreferences?.themeMode;
        if (typeof stored !== 'string') return;
        const next = stored.toLowerCase();
        if (!isPreference(next)) return;
        setPreferenceState(next);
        writeStoredPreference(next);
      })
      .catch(() => {
        /* A theme is not worth a visible failure: the local value is already applied and correct. */
      });
  }, [profile]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    writeStoredPreference(next);
    /*
     * Fire and forget, on purpose. The change is already on screen and already persisted locally,
     * so blocking the UI on a round trip, or rolling it back when the round trip fails, would both
     * be worse than a preference that syncs on the next successful save.
     */
    fetchGraphQLApiData(setUiPrefs(next)).catch(() => {});
  }, []);

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo<ThemeModeValue>(
    () => ({ preference, mode, setPreference }),
    [preference, mode, setPreference],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used inside ThemeModeProvider');
  return ctx;
}
